// @flow

import SearchHistoryApi from "../api/SearchHistoryApi/SearchHistoryApi";

// Service layer for search history management
// Provides graceful fallback to localStorage if API fails
// Feature 242, Req ID 404: Saved Recent Search History System

const STORAGE_KEY_PREFIX = 'searchHistory_';
const MAX_HISTORY_SIZE = 5;

class SearchHistoryService {
  searchHistoryApi: SearchHistoryApi;
  cache: Array<any>;
  userId: string;

  constructor(searchHistoryApi: SearchHistoryApi) {
    this.searchHistoryApi = searchHistoryApi;
    this.cache = [];
    this.userId = this.getCurrentUserId();
  }

  getCurrentUserId() {
    const userId = localStorage.getItem('user_id');
    return userId || 'default_user';
  }

  async getHistory(): Promise<Array<any>> {
    try {
      // Try API first
      const response = await this.searchHistoryApi.getHistory();
      if (response && Array.isArray(response)) {
        // Convert API response format to cache format
        // Handle both string array and object array formats
        this.cache = response.map(item => {
          if (typeof item === 'string') {
            return {
              query: item,
              timestamp: new Date().toISOString(),
            };
          }
          return {
            query: item.searchTerm || item.query,
            timestamp: item.createdAt || item.timestamp,
          };
        });
        return this.cache;
      }
    } catch (error) {
      console.warn('Failed to fetch search history from API, falling back to localStorage:', error);
    }

    // Fallback to localStorage
    return this.getHistoryFromStorage();
  }

  async addSearch(query: string): Promise<Array<any>> {
    if (!query || query.trim() === '') return this.cache;

    const trimmedQuery = query.trim();

    try {
      // Try API first
      const response = await this.searchHistoryApi.addSearch(trimmedQuery);
      if (response && Array.isArray(response)) {
        // Convert API response format to cache format
        // Handle both string array and object array formats
        this.cache = response.map(item => {
          if (typeof item === 'string') {
            return {
              query: item,
              timestamp: new Date().toISOString(),
            };
          }
          return {
            query: item.searchTerm || item.query,
            timestamp: item.createdAt || item.timestamp,
          };
        });
        return this.cache;
      }
    } catch (error) {
      console.warn('Failed to add search via API, falling back to localStorage:', error);
    }

    // Fallback to localStorage
    return this.addSearchToStorage(trimmedQuery);
  }

  getHistoryFromStorage(): Array<any> {
    try {
      const storageKey = STORAGE_KEY_PREFIX + this.userId;
      const historyJson = localStorage.getItem(storageKey);
      return historyJson ? JSON.parse(historyJson) : [];
    } catch (error) {
      console.error('Error retrieving search history from storage:', error);
      return [];
    }
  }

  addSearchToStorage(trimmedQuery: string): Array<any> {
    const history = this.getHistoryFromStorage();
    const newEntry = {
      query: trimmedQuery,
      timestamp: new Date().toISOString(),
    };

    history.unshift(newEntry);

    if (history.length > MAX_HISTORY_SIZE) {
      history.splice(MAX_HISTORY_SIZE);
    }

    this.saveHistoryToStorage(history);
    return history;
  }

  saveHistoryToStorage(history: Array<any>): void {
    try {
      const storageKey = STORAGE_KEY_PREFIX + this.userId;
      localStorage.setItem(storageKey, JSON.stringify(history));
    } catch (error) {
      console.error('Error saving search history to storage:', error);
    }
  }

  setUserId(userId: string): void {
    this.userId = userId;
    this.cache = [];
  }
}

export default SearchHistoryService;
